import { ref, computed, watch } from '@vue/composition-api'
import { useSettingStore } from '@/pinia/setting'

// Google Fonts that need to be loaded dynamically
const GOOGLE_FONTS = ['Nunito', 'Roboto', 'Open Sans', 'Poppins', 'Inter']

// Font fallback chains
const FONT_FALLBACKS = {
  'Rubik': "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
  'Arial': "'Arial', Helvetica, sans-serif",
  'Helvetica': "'Helvetica', Arial, sans-serif",
  'Times New Roman': "'Times New Roman', Times, serif",
  'Georgia': "'Georgia', 'Times New Roman', Times, serif",
  'Verdana': "'Verdana', Geneva, sans-serif",
  'Tahoma': "'Tahoma', Geneva, sans-serif",
  'Courier New': "'Courier New', Courier, monospace",
  'Consolas': "'Consolas', 'Liberation Mono', 'Courier New', monospace",
  'Liberation Mono': "'Liberation Mono', 'Courier New', Consolas, monospace",
  'Nunito': "'Nunito', 'Rubik', Arial, sans-serif",
  'Roboto': "'Roboto', Helvetica, Arial, sans-serif",
  'Open Sans': "'Open Sans', 'Segoe UI', Arial, sans-serif",
  'Poppins': "'Poppins', 'Segoe UI', Arial, sans-serif",
  'Inter': "'Inter', 'Segoe UI', Arial, sans-serif",
  'monospace': "monospace",
  'ESCPOS': "'Consolas', 'Liberation Mono', 'Courier New', monospace"
}

// Loaded Google Fonts cache
const loadedGoogleFonts = new Set()

export function usePrintFont() {
  const settingStore = useSettingStore()
  const isLoading = ref(false)

  // Get selected font from settings
  const selectedFont = computed(() => {
    return settingStore.val('faskes:font-cetakan') || 'Rubik'
  })

  // Get font family with fallbacks
  const fontFamily = computed(() => {
    const font = selectedFont.value
    return FONT_FALLBACKS[font] || FONT_FALLBACKS['Rubik']
  })

  // Check if font is a Google Font
  const isGoogleFont = computed(() => {
    return GOOGLE_FONTS.includes(selectedFont.value)
  })

  // Check if ESCPOS mode
  const isESCPOSMode = computed(() => {
    return selectedFont.value === 'ESCPOS'
  })

  // Load Google Font dynamically
  const loadGoogleFont = (fontName) => {
    if (!GOOGLE_FONTS.includes(fontName) || loadedGoogleFonts.has(fontName)) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      isLoading.value = true

      // Create link element for Google Font
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(' ', '+')}:wght@300;400;500;600;700&display=swap`

      link.onload = () => {
        loadedGoogleFonts.add(fontName)
        isLoading.value = false
        resolve()
      }

      link.onerror = () => {
        isLoading.value = false
        console.warn(`Failed to load Google Font: ${fontName}`)
        reject(new Error(`Failed to load font: ${fontName}`))
      }

      // Add to head if not already present
      if (!document.querySelector(`link[href*="${fontName.replace(' ', '+')}"]`)) {
        document.head.appendChild(link)
      } else {
        loadedGoogleFonts.add(fontName)
        isLoading.value = false
        resolve()
      }
    })
  }

  // Auto-load Google Font when selected font changes
  watch(selectedFont, async (newFont) => {
    if (GOOGLE_FONTS.includes(newFont)) {
      try {
        await loadGoogleFont(newFont)
      } catch (error) {
        console.warn('Google Font loading failed:', error.message)
      }
    }
  }, { immediate: true })

  // Get CSS styles for print elements
  const getPrintStyles = () => {
    const font = selectedFont.value

    if (font === 'ESCPOS') {
      return {
        fontFamily: FONT_FALLBACKS['ESCPOS'],
        fontSize: '12px',
        lineHeight: '1.2',
        letterSpacing: '0',
        fontWeight: 'normal',
        fontStyle: 'normal'
      }
    }

    return {
      fontFamily: fontFamily.value,
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
      fontWeight: 'inherit',
      fontStyle: 'inherit'
    }
  }

  // Get inline style string for components
  const getPrintStyleString = () => {
    const styles = getPrintStyles()
    return Object.entries(styles)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ')
  }

  // Get CSS class for ESCPOS mode
  const getPrintClass = () => {
    return isESCPOSMode.value ? 'print-escpos' : 'print-normal'
  }

  // Apply font to specific element
  const applyFontToElement = (element) => {
    if (!element) return

    const styles = getPrintStyles()
    Object.assign(element.style, styles)

    // Add ESCPOS class if needed
    if (isESCPOSMode.value) {
      element.classList.add('print-escpos')
    } else {
      element.classList.remove('print-escpos')
    }
  }

  // Preload all Google Fonts (for settings preview)
  const preloadAllGoogleFonts = async () => {
    const loadPromises = GOOGLE_FONTS.map(font => loadGoogleFont(font))
    try {
      await Promise.all(loadPromises)
    } catch (error) {
      console.warn('Some Google Fonts failed to load:', error)
    }
  }

  return {
    selectedFont,
    fontFamily,
    isGoogleFont,
    isESCPOSMode,
    isLoading,
    loadGoogleFont,
    getPrintStyles,
    getPrintStyleString,
    getPrintClass,
    applyFontToElement,
    preloadAllGoogleFonts,
    GOOGLE_FONTS,
    FONT_FALLBACKS
  }
}